import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Modal, message, Popconfirm, Row, Col, Input, Space } from 'antd';
import PartnerForm from '../components/PartnerForm';
import { getPartners, createPartner, updatePartner, deletePartner, crawlAllPartners } from '../utils/apiClient';
import { AppSelector } from '../components/AppSelector';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';

const Partners = () => {
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [crawlLog, setCrawlLog] = useState(null);
  const [crawlLogModalVisible, setCrawlLogModalVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (categoryFilter) params.category = categoryFilter;
      const res = await getPartners(params);
      setData(res.data);
    } catch (err) {
      message.error('Failed to fetch partners');
    }
    setLoading(false);
  }, [searchText, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'App', dataIndex: 'category', key: 'category' },
    { title: 'Review Rate', dataIndex: 'reviewRate', key: 'reviewRate' },
    { title: 'Review Count', dataIndex: 'reviewCount', key: 'reviewCount' },
    { title: 'Pricing', dataIndex: 'pricing', key: 'pricing' },
    {
      title: 'Short Description',
      dataIndex: 'shortDescription',
      key: 'shortDescription',
    },
    {
      title: 'Built for Shopify',
      dataIndex: 'isBuiltForShopify',
      key: 'isBuiltForShopify',
      render: (val) => (val ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this partner?" onConfirm={() => handleDelete(record._id)}>
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingItem(null);
    setModalVisible(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingItem(null);
  };

  const handleCrawlAll = async () => {
    setCrawlLoading(true);
    try {
      const res = await crawlAllPartners();
      message.success(res.data.message || 'Crawled all partners');
      setCrawlLog(res.data.log || []);
      setCrawlLogModalVisible(true);
      fetchData();
    } catch {
      message.error('Crawl all failed');
    }
    setCrawlLoading(false);
  };

  const handleFormSubmit = async (values) => {
    try {
      if (editingItem) {
        await updatePartner(editingItem._id, values);
        message.success('Partner updated');
      } else {
        await createPartner(values);
        message.success('Partner created');
      }
      fetchData();
      handleModalClose();
    } catch {
      message.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePartner(id);
      message.success('Partner deleted');
      fetchData();
    } catch {
      message.error('Delete failed');
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleReset = () => {
    setSearchText('');
    setCategoryFilter('');
    fetchData();
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0 }}>Partners</h2>
          <div>
            <Button type="primary" onClick={handleAdd} style={{ marginRight: 8 }}>
              Add Partner
            </Button>
            <Button onClick={handleCrawlAll} loading={crawlLoading}>
              Crawl All Partners
            </Button>
          </div>
        </div>
        <div className="table-actions">
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Search..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col xs={24} sm={8} md={4}>
              <AppSelector value={categoryFilter || undefined} onChange={setCategoryFilter} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Button type="primary" onClick={handleSearch}>
                  Search
                </Button>
                <Button onClick={handleReset}>Reset</Button>
                <Button icon={<ReloadOutlined />} onClick={() => fetchData()} title="Refresh" />
              </Space>
            </Col>
          </Row>
        </div>
        <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} />
        <Modal
          open={modalVisible}
          onCancel={handleModalClose}
          footer={null}
          destroyOnClose
          title={editingItem ? 'Edit Partner' : 'Add Partner'}
        >
          <PartnerForm item={editingItem} onClose={handleModalClose} onSubmit={handleFormSubmit} />
        </Modal>
        <Modal
          open={crawlLogModalVisible}
          onCancel={() => setCrawlLogModalVisible(false)}
          footer={null}
          title="Crawl Log"
        >
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {crawlLog && crawlLog.length > 0 ? (
              <ul>
                {crawlLog.map((entry, idx) => (
                  <li
                    key={idx}
                    style={{
                      color: entry.status === 'error' ? 'red' : 'green',
                    }}
                  >
                    <b>{entry.partner}</b> ({entry.shopifyAppUrl}): {entry.status}
                    {entry.error && <span> - {entry.error}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <div>No log available.</div>
            )}
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default Partners;

